import React, { useRef, useState } from 'react';

import { Button } from '@components/Button';
import MuiDialog from '@material-ui/core/Dialog';
import IconButton from '@material-ui/core/IconButton';
import Close from '@material-ui/icons/Close';
import { type ClassMap, makeMuiStyles } from '@styles';
import { formStrings } from '@utils/forms';
import clsx from 'clsx';
import { FormikProps } from 'formik';

const useStyles = makeMuiStyles((theme, common) => ({
  title: {
    ...common.textGray800,
    ...common.normalText,
    ...common.flexInline,
    ...common.fontWeight500,
    padding: '17px 20px',
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    alignItems: 'center',
  },
  body: {
    ...common.flexColumn,
    padding: '40px 30px',
    flexShrink: 1,
    minHeight: 0,
    overflow: 'auto', // So that body will be scrollable...
  },
  actions: {
    ...common.flexInline,
    justifyContent: 'center',
    borderTop: `1px solid ${theme.palette.grey[200]}`,
    padding: 16,
    columnGap: 64,
  },
  paper: {
    overflow: 'visible',
  },
}));

export interface FormStepProps {
  maxSteps: number;
  currentStep: number;
}

export interface FormProps {
  formikRef?: React.Ref<FormikProps<any>>;
  innerRef?: React.Ref<FormikProps<any>>;
  steps?: FormStepProps;
}

export interface FormDialogProps {
  open: boolean;
  onClose?: () => void;
  title: string;
  children: React.ReactElement<FormProps> | null;
  dialogProps?: Partial<React.ComponentProps<typeof MuiDialog>>;
  submitTitle?: string;
  prevStepTitle?: string;
  nextStepTitle?: string;
  alignTitle?: 'center' | 'left';
  maxSteps?: number;
  classes?: ClassMap<'body'>;
  nextDisabled?: boolean;
  actionsVisible?: boolean;
}

export const FormDialog: React.FC<FormDialogProps> = ({
  open,
  onClose,
  title,
  children,
  dialogProps,
  prevStepTitle = formStrings.prev,
  nextStepTitle = formStrings.next,
  submitTitle = 'OK',
  alignTitle = 'left',
  maxSteps = 0,
  classes: customClasses = {},
  nextDisabled = false,
  actionsVisible = true,
}) => {
  const classes = useStyles();
  const formikRef = useRef<FormikProps<any>>(null);

  const [currentStep, setCurrentStep] = useState(0);

  if (children === null) return null;

  const finalChild = React.cloneElement(children, {
    formikRef,
    innerRef: formikRef,
    steps: {
      maxSteps,
      currentStep,
    },
  });

  const closeDialog = () => {
    onClose?.();
    setCurrentStep(0);
  };

  const onCloseDialog = () => {
    if (formikRef.current?.dirty) {
      // eslint-disable-next-line no-restricted-globals, no-alert
      if (confirm(formStrings.promptClosing)) {
        closeDialog();
      }
    } else {
      closeDialog();
    }
  };

  return (
    <MuiDialog
      open={open}
      onClose={onCloseDialog}
      disablePortal
      classes={{ paper: classes.paper }}
      {...dialogProps}
    >
      <div className={classes.title}>
        {alignTitle === 'left' && <div style={{ width: '1rem' }} />}
        {alignTitle === 'center' && <div style={{ flex: 1 }} />}
        {title}
        <div style={{ flex: 1 }}>
          <IconButton style={{ float: 'right' }} onClick={onCloseDialog}>
            <Close fontSize="small" />
          </IconButton>
        </div>
      </div>
      <div className={clsx(classes.body, customClasses.body)}>{finalChild}</div>
      {actionsVisible && (
        <div className={classes.actions}>
          {currentStep > 0 && (
            <Button
              color="primary"
              size="small"
              shape="round"
              text={prevStepTitle}
              style={{ minWidth: 80 }}
              onClick={() => setCurrentStep((s) => s - 1)}
            />
          )}

          {currentStep >= maxSteps ? (
            <Button
              color="primary"
              size="small"
              shape="round"
              text={submitTitle}
              style={{ minWidth: 80 }}
              onClick={async () => {
                if (!formikRef.current?.isValid) {
                  formikRef.current?.submitForm();
                  confirm(formStrings.fixMessage);
                } else {
                  await formikRef.current?.submitForm();
                  if (formikRef.current?.isValid) {
                    closeDialog();
                  }
                }
              }}
            />
          ) : (
            <Button
              color="primary"
              size="small"
              shape="round"
              text={nextStepTitle}
              disabled={nextDisabled}
              style={{ minWidth: 80 }}
              onClick={() => setCurrentStep((s) => s + 1)}
            />
          )}
        </div>
      )}
    </MuiDialog>
  );
};
